// ===========================================
// TIPOS: Centro de Notificaciones In-App (CORE)
// ===========================================

import { rutaFichaCliente } from '../../lib/ruta-cliente'
import type { Permisos } from '../../types/roles'

// Tipos de notificacion
export type TipoNotificacion =
  | 'vencimiento'
  | 'tarea'
  | 'documento'
  | 'cliente'
  | 'sistema'
  | 'alerta'

// Notificacion in-app
export interface Notificacion {
  id: string
  user_email: string
  tipo: TipoNotificacion
  titulo: string
  mensaje: string
  enlace: string | null
  leida: boolean
  created_at: string
}

// Datos para crear notificacion
export interface NotificacionInsert {
  user_email: string
  tipo: TipoNotificacion
  titulo: string
  mensaje: string
  enlace?: string | null
}

// Datos para actualizar notificacion
export type NotificacionUpdate = Partial<Pick<Notificacion, 'leida'>>

// Configuracion visual por tipo
export const TIPO_NOTIFICACION_CONFIG: Record<TipoNotificacion, {
  label: string
  color: string
  bgClass: string
  icon: string
}> = {
  vencimiento: {
    label: 'Vencimiento',
    color: '#EF4444',
    bgClass: 'bg-red-100 text-red-800',
    icon: 'Calendar',
  },
  tarea: {
    label: 'Tarea',
    color: '#3B82F6',
    bgClass: 'bg-blue-100 text-blue-800',
    icon: 'CheckSquare',
  },
  documento: {
    label: 'Documento',
    color: '#10B981',
    bgClass: 'bg-green-100 text-green-800',
    icon: 'FileText',
  },
  cliente: {
    label: 'Cliente',
    color: '#8B5CF6',
    bgClass: 'bg-purple-100 text-purple-800',
    icon: 'Users',
  },
  sistema: {
    label: 'Sistema',
    color: '#6B7280',
    bgClass: 'bg-gray-100 text-gray-800',
    icon: 'Settings',
  },
  alerta: {
    label: 'Alerta',
    color: '#F59E0B',
    bgClass: 'bg-amber-100 text-amber-800',
    icon: 'AlertTriangle',
  },
}

// Filtros para notificaciones
export interface FiltrosNotificaciones {
  tipo?: TipoNotificacion
  leida?: boolean
  limite?: number
}

// Resumen de notificaciones para badge
export interface ResumenNotificaciones {
  total: number
  noLeidas: number
  porTipo: Record<TipoNotificacion, number>
}

// Helper para tiempo relativo
export function tiempoRelativo(fecha: string): string {
  const ahora = new Date()
  const fechaNotif = new Date(fecha)
  const diffMs = ahora.getTime() - fechaNotif.getTime()
  const diffSeg = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSeg / 60)
  const diffHoras = Math.floor(diffMin / 60)
  const diffDias = Math.floor(diffHoras / 24)

  if (diffSeg < 60) return 'Hace un momento'
  if (diffMin < 60) return `Hace ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`
  if (diffHoras < 24) return `Hace ${diffHoras} ${diffHoras === 1 ? 'hora' : 'horas'}`
  if (diffDias < 7) return `Hace ${diffDias} ${diffDias === 1 ? 'dia' : 'dias'}`

  return fechaNotif.toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
  })
}

// Helper interno: formatear fecha ISO/date a dd/MM/yyyy sin depender de date-fns.
// Parsea fechas YYYY-MM-DD como LOCAL (no UTC) para evitar el shift de día
// en timezones al oeste de UTC (ej: Argentina UTC-3). Para timestamps con hora
// explícita (ISO 8601 con T y Z), delega al constructor estándar de Date.
function formatearFechaCorta(fechaISO: string | null | undefined): string | null {
  if (!fechaISO) return null
  // Match explícito YYYY-MM-DD al inicio: parsear como fecha local
  const match = fechaISO.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/)
  let d: Date
  if (match) {
    const [, y, m, day] = match
    d = new Date(Number(y), Number(m) - 1, Number(day))
  } else {
    d = new Date(fechaISO)
  }
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Plantillas de notificacion
//
// ORR-613: una plantilla cuyo mensaje lleva importes declara `permiso`, el flag que hace falta para
// verlos. useNotificacionesModulo().notificar() descarta a los destinatarios que no lo tienen en la
// organización activa. El test de notificaciones.permiso exige el campo a toda plantilla con `$`.
export const PLANTILLAS_NOTIFICACION = {
  // Vencimientos
  vencimientoProximo: (cliente: string, impuesto: string, dias: number) => ({
    tipo: 'vencimiento' as TipoNotificacion,
    titulo: 'Vencimiento proximo',
    mensaje: `${cliente}: ${impuesto} vence en ${dias} ${dias === 1 ? 'dia' : 'dias'}`,
  }),

  vencimientoHoy: (cliente: string, impuesto: string) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Vencimiento HOY',
    mensaje: `${cliente}: ${impuesto} vence hoy`,
  }),

  vencimientoVencido: (cliente: string, impuesto: string) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Vencimiento VENCIDO',
    mensaje: `${cliente}: ${impuesto} esta vencido`,
  }),

  // ===========================================
  // Tareas (ORR-54)
  // ===========================================

  /** ORR-303: a alguien le delegaron un concepto de un cliente que puede no ser suyo. */
  conceptoAsignado: (nombreConcepto: string, nombreCliente: string) => ({
    tipo: 'vencimiento' as TipoNotificacion,
    titulo: 'CONCEPTO A TU CARGO',
    mensaje: `"${nombreConcepto}" de ${nombreCliente}`,
  }),

  /**
   * ORR-317: la versión en masa de la anterior. Una sola notificación con la cantidad, no
   * una por cliente — con 15 clientes de una vez, 15 notificaciones son ruido y no aportan
   * nada que la cantidad ya no diga.
   */
  conceptoAsignadoEnMasa: (nombreConcepto: string, cantidadClientes: number) => ({
    tipo: 'vencimiento' as TipoNotificacion,
    titulo: 'CONCEPTO A TU CARGO',
    mensaje: `"${nombreConcepto}" en ${cantidadClientes} cliente${cantidadClientes === 1 ? '' : 's'}`,
  }),

  tareaAsignada: (tituloTarea: string, fechaLimite: string | null | undefined) => {
    const fecha = formatearFechaCorta(fechaLimite)
    return {
      tipo: 'tarea' as TipoNotificacion,
      titulo: 'NUEVA TAREA',
      mensaje: fecha
        ? `"${tituloTarea}" — Vence ${fecha}`
        : `"${tituloTarea}" — Sin fecha límite`,
    }
  },

  /**
   * La versión en masa de la anterior. Una tarea por cliente de un grupo genera una
   * sola notificación con la cantidad, no una por tarea (mismo criterio que
   * `conceptoAsignadoEnMasa`).
   */
  tareaAsignadaEnMasa: (tituloTarea: string, cantidadClientes: number) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'NUEVA TAREA',
    mensaje: `"${tituloTarea}" en ${cantidadClientes} cliente${cantidadClientes === 1 ? '' : 's'}`,
  }),

  tareaCompletada: (tituloTarea: string, quienCompleto: string) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Tarea completada',
    mensaje: `${quienCompleto} completó: "${tituloTarea}"`,
  }),

  tareaCambioEstado: (tituloTarea: string, nuevoEstado: string, quienCambio: string) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Estado de tarea actualizado',
    mensaje: `${quienCambio} movió "${tituloTarea}" a ${nuevoEstado.replace('_', ' ')}`,
  }),

  tareaCambioPrioridad: (tituloTarea: string, nuevaPrioridad: string, quienCambio: string) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Prioridad cambiada',
    mensaje: `${quienCambio} cambió la prioridad de "${tituloTarea}" a ${nuevaPrioridad.toUpperCase()}`,
  }),

  tareaCambioFechaLimite: (tituloTarea: string, nuevaFecha: string | null | undefined, quienCambio: string) => {
    const fecha = formatearFechaCorta(nuevaFecha)
    return {
      tipo: 'tarea' as TipoNotificacion,
      titulo: 'Fecha límite actualizada',
      mensaje: fecha
        ? `${quienCambio} movió "${tituloTarea}" al ${fecha}`
        : `${quienCambio} quitó la fecha límite de "${tituloTarea}"`,
    }
  },

  tareaNuevoComentario: (tituloTarea: string, autor: string) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Nuevo comentario',
    mensaje: `${autor} comentó en "${tituloTarea}"`,
  }),

  tareaNuevaSubtarea: (tituloTarea: string, autor: string, tituloSubtarea: string) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Checklist actualizado',
    mensaje: `${autor} agregó "${tituloSubtarea}" a "${tituloTarea}"`,
  }),

  tareaNuevoRecordatorio: (tituloTarea: string, autor: string) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Nuevo recordatorio',
    mensaje: `${autor} agregó un recordatorio a "${tituloTarea}"`,
  }),

  tareaNuevoAdjunto: (tituloTarea: string, autor: string, nombreArchivo: string) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Nuevo adjunto',
    mensaje: `${autor} subió "${nombreArchivo}" a "${tituloTarea}"`,
  }),

  tareaEliminada: (tituloTarea: string, quienElimino: string) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Tarea eliminada',
    mensaje: `${quienElimino} eliminó: "${tituloTarea}"`,
  }),

  tareaReasignadaSaliente: (tituloTarea: string, nuevoAsignado: string) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Tarea reasignada',
    mensaje: `"${tituloTarea}" ahora está asignada a ${nuevoAsignado}`,
  }),

  tareaVencimientoProximo: (titulo: string, dias: number) => ({
    tipo: 'tarea' as TipoNotificacion,
    titulo: 'Tarea por vencer',
    mensaje: `"${titulo}" vence en ${dias} ${dias === 1 ? 'dia' : 'dias'}`,
  }),

  // ===========================================
  // Finanzas / Movimientos (ORR-57 Fase 2)
  // ===========================================

  pagoRegistrado: (cliente: string, monto: number, actor: string, concepto?: string | null) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Nuevo pago registrado',
    mensaje: `${actor} registró un pago de $${monto.toLocaleString('es-AR')} de ${cliente}${concepto ? ` — ${concepto}` : ''}`,
    permiso: 'verMovimientos' as const satisfies keyof Permisos,
  }),

  loteHonorariosGenerado: (mesAnio: string, creados: number, omitidos: number, montoTotal: number, actor: string) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Lote de honorarios generado',
    mensaje: `${actor} generó el lote ${mesAnio}: ${creados} movimientos creados${omitidos > 0 ? `, ${omitidos} omitidos` : ''}, total $${montoTotal.toLocaleString('es-AR')}`,
    permiso: 'verMovimientos' as const satisfies keyof Permisos,
  }),

  aumentoHonorariosAplicado: (actor: string, aplicados: number, ipcHasta: string) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Aumento por inflación aplicado',
    mensaje: `${actor} aumentó ${aplicados} ${aplicados === 1 ? 'precio' : 'precios'} de honorarios por inflación, con el IPC hasta ${ipcHasta}`,
  }),

  deudaVencida: (cliente: string, monto: number, concepto?: string | null) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Deuda vencida',
    mensaje: `${cliente} tiene una deuda vencida de $${monto.toLocaleString('es-AR')}${concepto ? ` — ${concepto}` : ''}`,
    permiso: 'verMovimientos' as const satisfies keyof Permisos,
  }),

  // ===========================================
  // Comercial / Presupuestos (ORR-59 Fase 4)
  // ===========================================

  presupuestoEnviado: (
    cliente: string,
    numero: number,
    total: number,
    actor: string,
  ) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Presupuesto enviado',
    mensaje: `${actor} envió el presupuesto #${numero} por $${total.toLocaleString('es-AR')} a ${cliente}`,
    permiso: 'verPresupuestos' as const satisfies keyof Permisos,
  }),

  presupuestoAceptado: (cliente: string, numero: number, total: number) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Presupuesto aceptado ✓',
    mensaje: `${cliente} aceptó el presupuesto #${numero} por $${total.toLocaleString('es-AR')}`,
    permiso: 'verPresupuestos' as const satisfies keyof Permisos,
  }),

  presupuestoRechazado: (cliente: string, numero: number) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Presupuesto rechazado',
    mensaje: `${cliente} rechazó el presupuesto #${numero}`,
    permiso: 'verPresupuestos' as const satisfies keyof Permisos,
  }),

  clienteAsignadoResponsable: (cantidadClientes: number, nombreCliente: string | null, actor: string) => ({
    tipo: 'cliente' as TipoNotificacion,
    titulo: cantidadClientes === 1 ? 'Nuevo cliente asignado' : `${cantidadClientes} clientes asignados`,
    mensaje: cantidadClientes === 1 && nombreCliente
      ? `${actor} te asignó como responsable de ${nombreCliente}`
      : `${actor} te asignó como responsable de ${cantidadClientes} clientes`,
  }),

  // ===========================================
  // Operacional / Documentos / Usuarios (ORR-60 Fase 5)
  // ===========================================

  documentoSubido: (
    cliente: string,
    documentoNombre: string,
    categoria: string | null,
    actor: string,
  ) => ({
    tipo: 'documento' as TipoNotificacion,
    titulo: 'Nuevo documento',
    mensaje: `${actor} subió "${documentoNombre}"${categoria ? ` (${categoria})` : ''} a ${cliente}`,
  }),

  usuarioInvitado: (emailInvitado: string, rol: string, actor: string) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Nuevo usuario invitado',
    mensaje: `${actor} invitó a ${emailInvitado} como ${rol}`,
  }),

  usuarioRolCambiado: (
    emailAfectado: string,
    rolAnterior: string,
    rolNuevo: string,
    actor: string,
  ) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Rol de usuario actualizado',
    mensaje: `${actor} cambió el rol de ${emailAfectado} de ${rolAnterior} a ${rolNuevo}`,
  }),

  usuarioBloqueado: (emailAfectado: string, bloqueado: boolean, actor: string) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: bloqueado ? 'Usuario bloqueado' : 'Usuario desbloqueado',
    mensaje: `${actor} ${bloqueado ? 'bloqueó' : 'desbloqueó'} el acceso de ${emailAfectado}`,
  }),

  usuarioEliminado: (emailAfectado: string, actor: string) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Usuario eliminado',
    mensaje: `${actor} eliminó a ${emailAfectado} de la organización`,
  }),

  permisosActualizados: (emailAfectado: string, actor: string) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Permisos actualizados',
    mensaje: `${actor} actualizó los permisos de ${emailAfectado}`,
  }),

  // Documentos
  documentoPorVencer: (documento: string, cliente: string, dias: number) => ({
    tipo: 'documento' as TipoNotificacion,
    titulo: 'Documento por vencer',
    mensaje: `${documento} de ${cliente} vence en ${dias} dias`,
  }),

  // Clientes
  clienteNuevo: (nombre: string) => ({
    tipo: 'cliente' as TipoNotificacion,
    titulo: 'Nuevo cliente',
    mensaje: `Se registro el cliente "${nombre}"`,
  }),

  // Sistema
  sistemaActualizacion: (mensaje: string) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Actualizacion del sistema',
    mensaje,
  }),

  // ===========================================
  // ORR-100: Sync de vencimientos fiscales (ARCA/COMARB)
  // ===========================================

  syncVencimientosDiffDetectado: (
    source: string,
    cantidadDiffs: number,
    topImpuesto: string | null,
    cantidadTop: number,
  ) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Cambios detectados en calendario fiscal',
    mensaje: topImpuesto
      ? `El sync con ${source} detectó ${cantidadDiffs} cambio(s) en vencimientos. Top: ${topImpuesto} (${cantidadTop} cambios).`
      : `El sync con ${source} detectó ${cantidadDiffs} cambio(s) en vencimientos.`,
    enlace: '/admin/sync-vencimientos',
  }),

  syncVencimientosClienteAfectado: (
    clienteNombre: string,
    clienteId: string,
    impuesto: string,
    periodo: string,
    fechaVieja: string,
    fechaNueva: string,
  ) => ({
    tipo: 'vencimiento' as TipoNotificacion,
    titulo: `Cambió un vencimiento de ${clienteNombre}`,
    mensaje: `${impuesto} período ${periodo}: fecha pasó de ${formatearFechaCorta(fechaVieja) ?? fechaVieja} a ${formatearFechaCorta(fechaNueva) ?? fechaNueva}.`,
    enlace: rutaFichaCliente(clienteId),
  }),

  syncVencimientosError: (source: string, status: string) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Error en sync de calendario fiscal',
    mensaje: `El sync con ${source} falló. Status: ${status}. Revisar /admin/sync-vencimientos`,
    enlace: '/admin/sync-vencimientos',
  }),

  // ===========================================
  // ORR-111: Auto-delegación AFIP + Letrita A
  // ===========================================

  afipDelegacionExitosa: (
    cliente: string,
    clienteId: string,
    cantidadServicios: number,
    actor: string,
  ) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Auto-delegación AFIP completada',
    mensaje: `${actor} delegó ${cantidadServicios} ${cantidadServicios === 1 ? 'servicio AFIP' : 'servicios AFIP'} de ${cliente} al estudio`,
    enlace: rutaFichaCliente(clienteId),
  }),

  afipDelegacionParcial: (
    cliente: string,
    clienteId: string,
    serviciosOk: number,
    serviciosFail: number,
    actor: string,
  ) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Auto-delegación AFIP parcial',
    mensaje: `${actor}: ${serviciosOk} OK / ${serviciosFail} fallaron en ${cliente}. Revisar audit AFIP.`,
    enlace: rutaFichaCliente(clienteId),
  }),

  afipDelegacionFallida: (
    cliente: string,
    clienteId: string,
    motivo: string,
    actor: string,
  ) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Auto-delegación AFIP falló',
    mensaje: `${actor} no pudo auto-delegar AFIP de ${cliente}: ${motivo}`,
    enlace: rutaFichaCliente(clienteId),
  }),

  afipLetritaAAcceso: (
    cliente: string,
    clienteId: string,
    actor: string,
  ) => ({
    tipo: 'sistema' as TipoNotificacion,
    titulo: 'Acceso al portal AFIP del cliente',
    mensaje: `${actor} abrió el portal AFIP como ${cliente} (Letrita A)`,
    enlace: rutaFichaCliente(clienteId),
  }),

  afipClaveInvalidada: (
    cliente: string,
    clienteId: string,
  ) => ({
    tipo: 'alerta' as TipoNotificacion,
    titulo: 'Clave AFIP inválida',
    mensaje: `La clave fiscal de ${cliente} fue marcada como inválida. Pedir actualización al cliente.`,
    enlace: rutaFichaCliente(clienteId),
  }),

  // ===========================================
  // ORR-351: la categoría de monotributo cambió al releer el padrón de ARCA
  // ===========================================

  categoriaMonotributoCambiada: (
    cliente: string,
    anterior: string | null,
    nueva: string,
    actor: string,
  ) => ({
    tipo: 'cliente' as TipoNotificacion,
    titulo: 'Cambio de categoría de monotributo',
    mensaje: `${cliente} pasó de ${anterior ?? 'sin categoría'} a ${nueva} según ARCA. Releído por ${actor}.`,
  }),

  /** Un solo aviso para el lote: nombra hasta `maxNombres` clientes y cuenta el resto. */
  categoriasMonotributoCambiadasLote: (
    cambios: { nombre: string; anterior: string | null; nueva: string }[],
    actor: string,
    maxNombres = 5,
  ) => {
    const visibles = cambios
      .slice(0, maxNombres)
      .map((c) => `${c.nombre}: ${c.anterior ?? 'sin categoría'} → ${c.nueva}`)
    const resto = cambios.length - visibles.length
    return {
      tipo: 'cliente' as TipoNotificacion,
      titulo: `${cambios.length} clientes cambiaron de categoría en ARCA`,
      mensaje: `${actor} releyó el padrón. ${visibles.join(' · ')}${resto > 0 ? ` y ${resto} más` : ''}.`,
    }
  },
}

// ===========================================
// ORR-69: Trazabilidad de origen + destinatarios
// ===========================================

/**
 * Trazabilidad del evento que disparo una notificacion.
 * Se persiste en notificaciones.origen (jsonb).
 */
export interface NotificacionOrigen {
  canal: 'web' | 'pwa' | 'cron' | 'sistema'
  actor_email: string
  tabla_fuente?: string           // ej: 'tareas', 'comentarios_tarea', 'movimientos'
  registro_id?: string            // ej: tarea_id, comentario_id
  conversacion_id?: string        // para chat
  mensaje_id?: string              // para chat
}

/**
 * Spec de destinatario aceptada por destinatariosResolver.
 *
 * Formatos soportados:
 * - 'user:email@x.com'          un usuario especifico
 * - 'org:uuid-org'               todos los miembros activos de la org (excepto actor)
 * - 'responsables:uuid-cli'      responsables del cliente (fallback a admins)
 * - 'origin'                     responde al origen del evento (requiere origen.actor_email)
 */
export type DestinatarioSpec = string

/**
 * Canales soportados por el sistema de preferencias_notificaciones.
 * El canal 'email' se sumara cuando exista emisor de email (fuera del alcance de ORR-69).
 */
export type CanalNotificacion = 'in_app' | 'push'

/**
 * Fila de preferencias_notificaciones.
 * NULL en la tabla = default (enviar), mantiene comportamiento pre-ORR-69.
 */
export interface PreferenciaNotificacion {
  id: string
  user_email: string
  tipo: TipoNotificacion
  canal: CanalNotificacion
  activo: boolean
  created_at: string | null
  updated_at: string | null
}
